import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { Button } from "@mui/material";
import { Print } from "@mui/icons-material";
import LoadingPanel from "../../components/common/LoadingPanel";
import ErrorMessage from "../../components/common/ErrorMessage";
import localize from "../../common/localize";
import useGlobalContext from "../../hooks/useGlobalContext";
import SettlementReportBody from "./components/SettlementReportBody";
import SettlementReportSummary from "./components/SettlementReportSummary";
import { parseSettlementFilterParams } from "./components/settlementFilterUtils";

const normalize = (body) =>
  Array.isArray(body) ? body : (body?.Data ?? body?.responseData ?? []);

export default function SettlementReport() {
  const { config, SETTLEMENT_ENDPOINT } = useGlobalContext();
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();

  const [settlement, setSettlement] = useState(null);
  const [details, setDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const componentRef = useRef();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await axios.get(`${SETTLEMENT_ENDPOINT}/${uuid}`, config);
        const settlementData =
          resp?.data?.Data ?? resp?.data?.responseData ?? resp?.data ?? null;
        const detResp = await axios.get(
          `${SETTLEMENT_ENDPOINT}/${uuid}/detail`,
          config,
        );
        if (!mountedRef.current) return;
        setSettlement(settlementData);
        setDetails(normalize(detResp?.data));
      } catch (error) {
        if (!mountedRef.current) return;
        setErrorMessage(error?.message ?? localize.error_unexpected);
        setShowErrorModal(true);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    };
    load();
  }, [uuid]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@media print {
      @page {
        size: 600mm 500mm;
        margin: 0;
      }
      a[href]:after {
        content: none !important;
      }
      .print-row {
        break-inside: avoid;
      }
    }`,
  });

  if (isLoading) {
    return <LoadingPanel text={localize.loading} />;
  }

  const filters = parseSettlementFilterParams(searchParams);

  return (
    <div className="w-full flex items-center justify-center align-center">
      <div className="w-full flex-col" style={{ width: "80%" }}>
        <div className="w-full flex-col" ref={componentRef}>
          {showErrorModal && (
            <ErrorMessage
              message={errorMessage}
              title={localize.error_unexpected}
              showModal={showErrorModal}
              setShowModal={setShowErrorModal}
            />
          )}

          <div style={{ breakAfter: "page", pageBreakAfter: "always" }}>
            <SettlementReportSummary details={details} filters={filters} />
          </div>

          <SettlementReportBody
            settlement={settlement}
            details={details}
            filters={filters}
          />
        </div>

        <div className="flex w-full justify-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handlePrint}
            style={{ marginTop: "20px", marginBottom: "30px" }}
          >
            <span className="mr-2">{localize.print}</span>
            <Print />
          </Button>
        </div>
      </div>
    </div>
  );
}
